import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import apiService from '../services/api';
import PageBackground from '../components/PageBackground';
import VastuSubHeader from '../components/VastuSubHeader';
import './ConsultantProfilePage.css'; // <-- Import the new dedicated CSS

const ConsultantProfilePage = () => {
  const { id } = useParams();
  const [data, setData] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
    apiService.getVastuConsultantProfile(id)
      .then(res => setData(res.data))
      .catch(err => console.error(err))
      .finally(() => setIsLoading(false));
  }, [id]);

  if (isLoading) return <div className="cp-page-wrapper"><div className="loading-spinner" style={{marginTop: '100px'}}>Loading profile...</div></div>;
  if (!data) return <div className="cp-page-wrapper"><div className="empty-state" style={{marginTop: '100px'}}>Consultant not found.</div></div>;

  const { consultant, recentReviews } = data;
  const bgUrl = 'https://images.unsplash.com/photo-1558535235-943a5c518b2b?auto=format&fit=crop&w=1920&q=80';
  const getInitials = (name = '') => name.split(' ').map(p => p[0]).join('').slice(0, 2).toUpperCase() || 'VA';

  return (
    <div className="cp-page-wrapper">
      <PageBackground imageUrl={bgUrl} />
      <VastuSubHeader />

      {/* --- FULL WIDTH HERO BANNER --- */}
      <section className="cp-hero-banner">
        <motion.div 
          className="cp-hero-content"
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          <div className="cp-avatar-container">
            {consultant.profilePhoto?.url ? (
              <img src={consultant.profilePhoto.url} alt={consultant.displayName} />
            ) : (
              <span>{getInitials(consultant.displayName)}</span>
            )}
          </div>

          <div className="cp-header-info">
            <h2 className="cp-name">{consultant.displayName}</h2>
            <p className="cp-headline">{consultant.profileHeadline || 'Expert Vastu Consultant'}</p>
            
            <div className="cp-hero-stats">
              <div className="cp-stat-item">
                <i className="fas fa-star" style={{ color: '#f59e0b' }}></i>
                <span>{consultant.ratingAverage || 5.0} Rating</span>
              </div>
              <div className="cp-stat-item">
                <i className="fas fa-comment"></i>
                <span>{consultant.ratingCount || 0} Reviews</span>
              </div>
              <div className="cp-stat-item">
                <i className="fas fa-briefcase"></i>
                <span>{consultant.experienceYears || 0}+ Years Exp</span>
              </div>
            </div>
          </div>
        </motion.div>
      </section>

      {/* --- MAIN CONTENT GRID --- */}
      <div className="cp-main-container">
        
        {/* LEFT COLUMN: BIO & EXPERTISE */}
        <main className="cp-content-left">
          <motion.section 
            className="cp-section"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.2 }}
          >
            <h2 className="cp-section-title">About Me</h2>
            <p className="cp-bio-text">
              {consultant.bio || "Dedicated to bringing harmony and prosperity to your spaces through authentic Vastu principles. Book a consultation today to start your journey towards a balanced life."}
            </p>
          </motion.section>

          <motion.section 
            className="cp-section"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.3 }}
          >
            <h2 className="cp-section-title">Expertise & Languages</h2>
            
            <h4 style={{ color: '#555', marginBottom: '10px' }}><i className="fas fa-award"></i> Specializations</h4>
            <div className="cp-tags-container">
              {(consultant.specializations || []).map(s => <span key={s} className="cp-tag">{s}</span>)}
              {(!consultant.specializations || consultant.specializations.length === 0) && <span className="cp-tag">Vastu Shastra</span>}
            </div>

            <h4 style={{ color: '#555', marginBottom: '10px', marginTop: '1.5rem' }}><i className="fas fa-language"></i> Languages Spoken</h4>
            <div className="cp-tags-container" style={{ marginBottom: 0 }}>
              {(consultant.languages || []).map(l => <span key={l} className="cp-lang-tag">{l}</span>)}
            </div>
          </motion.section>

          <motion.section 
            className="cp-section"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.4 }}
          >
            <h2 className="cp-section-title">Client Reviews ({recentReviews.length})</h2>
            {recentReviews.length > 0 ? (
              <div className="cp-reviews-list">
                {recentReviews.map((review, idx) => (
                  <div key={review._id} className="cp-review-item">
                    <div className="cp-review-header">
                      <div className="cp-reviewer">
                         <div className="cp-reviewer-initial">{review.customer.fullName[0].toUpperCase()}</div>
                         <strong>{review.customer.fullName}</strong>
                      </div>
                      <span className="cp-review-stars">
                        {'★'.repeat(review.rating.score)}{'☆'.repeat(5 - review.rating.score)}
                      </span>
                    </div>
                    <p className="cp-review-text">"{review.rating.review}"</p>
                    <span className="cp-review-date">{new Date(review.rating.updatedAt).toLocaleDateString('en-IN', { year: 'numeric', month: 'short', day: 'numeric'})}</span>
                  </div>
                ))}
              </div>
            ) : (
              <p style={{ color: '#777', fontStyle: 'italic' }}>No written reviews yet. Be the first to consult and leave feedback!</p>
            )}
          </motion.section>
        </main>

        {/* RIGHT COLUMN: STICKY BOOKING CARD */}
        <aside className="cp-sidebar">
          <motion.div 
            className="cp-booking-card"
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.5, delay: 0.4 }}
          >
            <h3>Start Your Journey</h3>
            <p>Book a private consultation with <strong>{consultant.displayName.split(' ')[0]}</strong> to align your space and bring positivity into your life.</p>
            
            <Link to={`/vastu/book?consultantId=${consultant._id}`} className="cp-book-btn">
              <i className="fas fa-calendar-check"></i> Book Consultation
            </Link>
          </motion.div>
        </aside>

      </div>
    </div>
  );
};

export default ConsultantProfilePage;